"use client";

import Section from "@/components/section";
import { Button, buttonVariants } from "@/components/ui/button";
import { motion } from "src/lib/motion";
import { useState, useEffect, useRef } from "react";
import AIConsultingTeam from "@/components/sections/ai-consulting-team";
import { Check, Brain, LineChart, FileText, Users, Building2, Hospital, ShoppingBag, Factory, Truck, CircleGauge, BookOpenCheck, Cpu, Network, Shield, Zap, BarChart3 } from "lucide-react";
import { Icons } from "@/components/icons";
import BlurFade from "@/components/magicui/blur-fade";
import DotPattern from "@/components/magicui/dot-pattern";
import Ripple from "@/components/magicui/ripple";
import Image from "next/image";
import { Card, CardContent } from "@/components/ui/card";
import Link from "next/link";
import { cn } from "@/lib/utils";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import TabSwitchSection from "@/components/ui/tab-switch-section";
import ServicesSection from "@/components/sections/services";
import { Carousel, CarouselContent, CarouselItem, CarouselNext, CarouselPrevious } from "@/components/ui/carousel";
import { useTranslations } from "@/lib/hooks/use-translations";
import { useParams } from "next/navigation";
import { Locale } from "@/lib/i18n";

const ease = [0.16, 1, 0.3, 1];

// Import Images
import industrie40HeaderImage from "@/assets/images/industrie40header.png";
import productionLineImage from "@/assets/images/photo-automobile-production-line-welding-car-body-modern-car-assembly-plant-auto-industry.jpg";
import logisticsImage from "@/assets/images/technological-futuristic-holograms-logistics-means-transport.jpg";
import dashboardImage from "@/assets/images/dashboard.png";
import containerImage from "@/assets/images/container.svg";
import electronicsImage from "@/assets/images/closeup-green-circuit-boards-automated-assembly-line-detailed-electronic-components-with-glowing-lights-showcase-technology.jpg";
import pharmacistImage from "@/assets/images/pharmacist-day-celebration-with-male-pharmacist.jpg";
import foodBevImage from "@/assets/images/technologist-with-laptop-his-hands-make-set-up-production-line-while-standing-department-dairy-factory.jpg";
import { serializeJsonLd } from "@/lib/sanitize";

function Hero() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params.locale as Locale;

  return (
    <Section
      id="hero"
      className="relative overflow-hidden bg-neutral-100 dark:bg-neutral-900 py-12 sm:py-24"
    >
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 lg:gap-12 items-center">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
            className="text-center lg:text-left order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease }}
              className="space-y-6"
            >
              <h1 className="text-4xl md:text-6xl font-bold text-foreground">
                {t('pages.solutions.industry-4-0.hero.title')}
              </h1>
              <h2 className="text-2xl md:text-3xl font-semibold text-foreground mt-4">
                {t('pages.solutions.industry-4-0.hero.subtitle')}
              </h2>
              <p className="text-xl text-muted-foreground">
                {t('pages.solutions.industry-4-0.hero.description')}
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4, duration: 0.8, ease }}
              className="flex flex-col sm:flex-row w-full gap-4 mt-6 lg:justify-start sm:justify-center"
            >
              <Link
                href={`/${locale}/contact`}
                className={cn(
                  buttonVariants({ size: "lg" }),
                  "w-full sm:w-auto text-background flex gap-2"
                )}
              >
                <Icons.logoIcon className="h-6 w-6" />
                {t('pages.solutions.industry-4-0.hero.cta.primary')}
              </Link>
              <button
                onClick={() => document.getElementById("why-industry-4-0")?.scrollIntoView({ behavior: "smooth" })}
                className={cn(
                  buttonVariants({ variant: "outline", size: "lg" }),
                  "w-full sm:w-auto"
                )}
              >
                {t('pages.solutions.industry-4-0.hero.cta.secondary')}
              </button>
            </motion.div>

          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, ease }}
            className="relative order-2"
          >
            <DotPattern
              className="absolute inset-0"
              width={40}
              height={40}
              radius={1.5}
              offset={20}
              color="var(--primary)"
              opacity={0.1}
            />
            <div className="w-full h-[250px] sm:h-[400px] rounded-lg relative z-10 bg-neutral-100 dark:bg-neutral-900">
              <Image
                src={industrie40HeaderImage}
                alt={t('pages.solutions.industry-4-0.hero.imageAlt')}
                className="w-full h-full object-contain"
                priority
                width={800}
                height={600}
              />
            </div>
          </motion.div>
        </div>
      </div>
    </Section>
  );
}

const SWIPE_THRESHOLD = 50;
function WhyIndustry40() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params.locale as Locale;
  const [percentage, setPercentage] = useState(0);
  const [isInView, setIsInView] = useState(false);
  const [activeSlide, setActiveSlide] = useState(0);
  const intervalRef = useRef<NodeJS.Timeout>();
  const totalSlides = 5;
  const startX = useRef(0);
  const endX = useRef(0);

  useEffect(() => {
    if (isInView && percentage < 51) {
      intervalRef.current = setInterval(() => {
        setPercentage(prev => {
          if (prev >= 51) {
            clearInterval(intervalRef.current);
            return 51;
          }
          return Math.min(51, prev + Math.ceil((51 - prev) / 15));
        });
      }, 40);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isInView, percentage]);

  const data = t<Array<{
    id: number;
    title: string;
    content: string;
    icon: string;
  }>>('pages.solutions.industry-4-0.why.benefits');

  const iconMap: Record<string, React.ReactNode> = {
    'CircleGauge': <CircleGauge className="h-6 w-6 text-primary" />,
    'Shield': <Shield className="h-6 w-6 text-primary" />,
    'Cpu': <Cpu className="h-6 w-6 text-primary" />,
    'Network': <Network className="h-6 w-6 text-primary" />,
    'Zap': <Zap className="h-6 w-6 text-primary" />,
  };

  const handlePrevious = () => {
    setActiveSlide((prev) => (prev === 0 ? totalSlides - 1 : prev - 1));
  };

  const handleNext = () => {
    setActiveSlide((prev) => (prev === totalSlides - 1 ? 0 : prev + 1));
  };
  const handleTouchStart = (e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    endX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = endX.current - startX.current;
    if (diff > SWIPE_THRESHOLD) {
      handlePrevious()
    } else if (diff < -SWIPE_THRESHOLD) {
      handleNext()
    }
  };

  const handleMouseDown = (e: React.MouseEvent) => {
    startX.current = e.clientX;
    endX.current = e.clientX;
    const handleMouseMove = (e: MouseEvent) => {
      endX.current = e.clientX;
    };
    const handleMouseUp = () => {
      const diff = endX.current - startX.current;
      if (diff > SWIPE_THRESHOLD) {
        handlePrevious()
      } else if (diff < -SWIPE_THRESHOLD) {
        handleNext()
      }
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  return (
    <Section
      id="why-industry-4-0"
      className="bg-muted/30"
      mainCta={{
        text: t('pages.solutions.industry-4-0.why.cta.primary'),
        href: `/${locale}/contact`,
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
      containerClassName=""
      largerTopGap
      secondaryCta={{
        text: t('pages.solutions.industry-4-0.why.cta.secondary'),
        onClick: () => document.getElementById("ai-consulting-team")?.scrollIntoView({ behavior: "smooth" })
      }}
    >
      <div className="text-center px-6 mb-12">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-3xl font-bold mb-4 break-words"
        >
          {t('pages.solutions.industry-4-0.why.title')}
        </motion.h2>
      </div>

      {/* Stat Callout */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        viewport={{ once: true }}
        className="max-w-3xl mx-auto mb-16 bg-muted/50 rounded-xl p-6 sm:p-8 relative mx-4 sm:mx-auto"
      >
        <div className="absolute left-6 top-6 text-4xl text-primary/20">
          &quot;
        </div>
        <div className="text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.5 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            onViewportEnter={() => setIsInView(true)}
            transition={{ type: "spring", stiffness: 100, damping: 10 }}
            className="text-7xl md:text-[12rem] font-bold text-primary mb-4 tracking-tight relative"
          >
            <motion.span
              className="absolute inset-0 blur-2xl bg-primary/20 rounded-full"
              animate={{
                opacity: [0.2, 0.4, 0.2],
                scale: [0.9, 1.1, 0.9],
              }}
              transition={{
                duration: 3,
                repeat: Infinity,
                ease: "easeInOut"
              }}
            />
            <span className="relative bg-clip-text text-transparent bg-gradient-to-r from-primary via-primary to-primary/70">
              {percentage}%
            </span>
          </motion.div>
          <p className="text-xl text-muted-foreground">
            {t('pages.solutions.industry-4-0.why.stat.text')}
          </p>
          <div className="mt-4 text-sm text-muted-foreground flex items-center justify-center gap-2">
            <span>{t('pages.solutions.industry-4-0.why.stat.source')}</span>
            <Link target="_blank" href="https://www.capgemini.com/gb-en/wp-content/uploads/sites/3/2019/12/Report-%E2%80%93-AI-in-MfG-Ops.pdf" className="inline-flex items-center hover:text-primary">
              <FileText className="h-4 w-4 ml-1" />
            </Link>
          </div>
        </div>
      </motion.div>

      {/* Benefits Carousel */}
      <div className="relative max-w-4xl mx-auto ">
        <div
          className="w-full"
          role="region"
          aria-roledescription="carousel"
          aria-label={t('pages.solutions.industry-4-0.why.carouselLabel')}
        >
          <div className="overflow-hidden cursor-grab select-none"
          onTouchStart={handleTouchStart}
          onTouchMove={handleTouchMove}
          onTouchEnd={handleTouchEnd}
          onMouseDown={handleMouseDown}>
            {/* Only render the active slide */}
            {data.map((item: any, index: number) => (
              index === activeSlide && (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, x: 100 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -100 }}
                  transition={{ duration: 0.3 }}
                >
                  <motion.div
                    className="relative bg-background/50 hover:bg-background/80 transition-colors rounded-xl p-5 sm:p-8 border border-border/50 sm:mx-0"
                  >
                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6">
                      <div className="flex justify-center sm:justify-start sm:mt-1">
                        <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                          {iconMap[item.icon]}
                        </div>
                      </div>
                      <div>
                        <h3 className="text-xl sm:text-2xl font-semibold mb-2 sm:mb-4 text-center sm:text-left">{item.title}</h3>
                        <p className="text-base sm:text-lg text-muted-foreground max-md:text-center leading-relaxed">{item.content}</p>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )
            ))}
          </div>

          {/* Navigation controls */}
          <button
            onClick={handlePrevious}
            className="absolute left-0 sm:left-12 lg:left-0 top-[99%] -translate-y-1/2 sm:-translate-x-12 bg-background/80 border border-primary rounded-full p-2 flex items-center justify-center duration-200 md:hover:bg-primary md:hover:text-white lg:top-[39%]"
            aria-label="Previous slide"
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
              <path d="M8.84182 3.13514C9.04327 3.32401 9.05348 3.64042 8.86462 3.84188L5.43521 7.49991L8.86462 11.1579C9.05348 11.3594 9.04327 11.6758 8.84182 11.8647C8.64036 12.0535 8.32394 12.0433 8.13508 11.8419L4.38508 7.84188C4.20477 7.64955 4.20477 7.35027 4.38508 7.15794L8.13508 3.15794C8.32394 2.95648 8.64036 2.94628 8.84182 3.13514Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </button>
          <button
            onClick={handleNext}
            className="absolute right-0 lg:right-0 sm:right-12 top-[99%] -translate-y-1/2 sm:translate-x-12 bg-background/80 border border-primary rounded-full p-2 flex items-center justify-center duration-200 md:hover:bg-primary md:hover:text-white lg:top-[39%]"
            aria-label="Next slide"
          >
            <svg width="15" height="15" viewBox="0 0 15 15" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-4 w-4">
              <path d="M6.1584 3.13508C6.35985 2.94621 6.67627 2.95642 6.86514 3.15788L10.6151 7.15788C10.7954 7.3502 10.7954 7.64949 10.6151 7.84182L6.86514 11.8418C6.67627 12.0433 6.35985 12.0535 6.1584 11.8646C5.95694 11.6757 5.94673 11.3593 6.1356 11.1579L9.565 7.49985L6.1356 3.84182C5.94673 3.64036 5.95694 3.32394 6.1584 3.13508Z" fill="currentColor" fillRule="evenodd" clipRule="evenodd"></path>
            </svg>
          </button>

          {/* Slide indicator */}
          <div className="my-10 flex justify-center gap-2">
            <div className="flex gap-1">
              {data.map((_: any, index: number) => (
                <button
                  key={index}
                  onClick={() => setActiveSlide(index)}
                  className={`w-2 h-2 rounded-full ${index === activeSlide ? 'bg-primary' : 'bg-primary/30'}`}
                  aria-label={`Go to slide ${index + 1}`}
                  aria-current={index === activeSlide ? 'true' : 'false'}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </Section>
  );
}

function Services() {
  const { t } = useTranslations();

  const industry40Services = t<Array<{
    title: string;
    description: string;
    features: string[];
    icon: string;
  }>>('pages.solutions.industry-4-0.services.items');

  const iconMap: Record<string, React.ReactNode> = {
    'Factory': <Factory className="h-6 w-6" />,
    'Shield': <Shield className="h-6 w-6" />,
    'Network': <Network className="h-6 w-6" />,
    'Truck': <Truck className="h-6 w-6" />,
    'BarChart3': <BarChart3 className="h-6 w-6" />,
    'Users': <Users className="h-6 w-6" />,
  };

  return (
    <Section
      id="services"
      title={t('pages.solutions.industry-4-0.services.title')}
      subtitle={t('pages.solutions.industry-4-0.services.subtitle')}
      className="bg-muted/30"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {industry40Services.map((service: any, index: number) => (
          <motion.div
            key={index}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: index * 0.1 }}
            viewport={{ once: true }}
          >
            <Card className="h-full hover:shadow-lg transition-shadow">
              <CardContent className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center text-primary">
                    {iconMap[service.icon]}
                  </div>
                  <h3 className="text-xl font-semibold">{service.title}</h3>
                </div>
                <p className="text-muted-foreground mb-4">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature: string, idx: number) => (
                    <li key={idx} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </motion.div>
        ))}
      </div>
    </Section>
  );
}

function Industries() {
  const { t } = useTranslations();

  const industryCategories = t<Array<{
    id: string;
    label: string;
    icon: string;
  }>>('pages.solutions.industry-4-0.industries.categories');

  const industryContents = t<Record<string, {
    title: string;
    description: string;
    features: string[];
    image: string;
    url: string;
  }>>('pages.solutions.industry-4-0.industries.contents');

  const iconMap: Record<string, React.ReactNode> = {
    'Factory': <Factory className="h-4 w-4" />,
    'Cpu': <Cpu className="h-4 w-4" />,
    'Hospital': <Hospital className="h-4 w-4" />,
    'ShoppingBag': <ShoppingBag className="h-4 w-4" />,
    'Zap': <Zap className="h-4 w-4" />,
  };

  // Add icons to categories
  const categoriesWithIcons = industryCategories.map((cat: any) => ({
    ...cat,
    icon: iconMap[cat.icon]
  }));

  // Map image paths
  const imageMap: Record<string, any> = {
    'productionLine': productionLineImage.src,
    'electronics': electronicsImage.src,
    'pharmacist': pharmacistImage.src,
    'foodBev': foodBevImage.src,
    'logistics': logisticsImage.src,
  };

  const contentsWithImages = Object.entries(industryContents).reduce((acc, [key, value]: [string, any]) => {
    acc[key] = {
      ...value,
      image: imageMap[value.image] || productionLineImage.src
    };
    return acc;
  }, {} as Record<string, any>);

  return (
      <TabSwitchSection
        title={t('pages.solutions.industry-4-0.industries.title')}
        subtitle={t('pages.solutions.industry-4-0.industries.subtitle')}
        categories={categoriesWithIcons}
        contents={contentsWithImages}
        navigationStyles="lg:mb-24"
        defaultTab="automotive"
        className="bg-muted/30"
      />
  );
}

function CaseStudy() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params.locale as Locale;

  return (
    <Section
      id="case-study"
      className="bg-background"
      mainCta={{
        text: t('pages.solutions.industry-4-0.case-study.cta'),
        href: `/${locale}/contact`,
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
    >
      <div className="max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl font-bold mb-4">{t('pages.solutions.industry-4-0.case-study.title')}</h2>
          <p className="text-xl text-muted-foreground">
            {t('pages.solutions.industry-4-0.case-study.subtitle')}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.2 }}
          viewport={{ once: true }}
          className="bg-muted/30 rounded-xl p-8"
        >
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div>
              <h3 className="text-xl font-semibold mb-4">{t('pages.solutions.industry-4-0.case-study.challenge.title')}</h3>
              <p className="text-muted-foreground">
                {t('pages.solutions.industry-4-0.case-study.challenge.description')}
              </p>
            </div>
            <div>
              <h3 className="text-xl font-semibold mb-4">{t('pages.solutions.industry-4-0.case-study.approach.title')}</h3>
              <p className="text-muted-foreground">
                {t('pages.solutions.industry-4-0.case-study.approach.description')}
              </p>
            </div>
          </div>

          <div className="border-t pt-8">
            <h3 className="text-xl font-semibold mb-6 text-center">{t('pages.solutions.industry-4-0.case-study.benefits.title')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl mx-auto">
              {t<string[]>('pages.solutions.industry-4-0.case-study.benefits.items').map((benefit: string, index: number) => (
                <div key={index} className="flex items-start gap-3">
                  <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                  <span className="text-muted-foreground">{benefit}</span>
                </div>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </Section>
  );
}

function FAQ() {
  const { t } = useTranslations();

  const faqs = t<Array<{
    id: string;
    question: string;
    answer: string;
  }>>('pages.solutions.industry-4-0.faq.items');

  return (
    <Section
      id="faq"
      title={t('pages.solutions.industry-4-0.faq.title')}
      subtitle={t('pages.solutions.industry-4-0.faq.subtitle')}
      className="bg-muted/30 max-md:!pt-8"
    >
      <div className="mx-auto md:max-w-[800px] lg:my-12">
        <Accordion
          type="single"
          collapsible
          className="flex w-full flex-col items-center justify-center space-y-2"
        >
          {faqs.map((faq: any, idx: number) => (
            <AccordionItem
              key={idx}
              value={faq.question}
              className="w-full border rounded-lg overflow-hidden shadow-lg hover:bg-primary/10 transition durantion-200 [&[data-state=open]]:bg-primary/10"
            >
              <AccordionTrigger className="px-4">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="px-4">{faq.answer}</AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </Section>
  );
}

function FinalCTA() {
  const { t } = useTranslations();
  const params = useParams();
  const locale = params.locale as Locale;

  return (
    <Section
      id="final-cta"
      className="bg-primary/5"
      mainCta={{
        text: t('pages.solutions.industry-4-0.final-cta.cta'),
        href: `/${locale}/contact`,
        icon: <Icons.logoIcon className="h-6 w-6" />
      }}
    >
      <div className="text-center max-w-3xl mx-auto sm:px-6">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="text-3xl font-bold mb-4 break-words"
        >
          {t('pages.solutions.industry-4-0.final-cta.title')}
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-muted-foreground mb-8"
        >
          {t('pages.solutions.industry-4-0.final-cta.description')}
        </motion.p>
      </div>
    </Section>
  );
}

export default function Industry40Page() {
  const params = useParams();
  const locale = params.locale as Locale;

  return (
    <main className="flex flex-col min-h-screen">
      <Hero />
      <WhyIndustry40 />
      <AIConsultingTeam locale={locale} />
      <Services />
      <Industries />
      <CaseStudy />
      <FAQ />
      <FinalCTA />

      {/* Schema.org structured data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "SavagesCorp",
            "url": "https://savagescorp.ai",
            "logo": "https://savagescorp.ai/logo.png",
            "sameAs": [
              "https://twitter.com/savagescorp",
              "https://linkedin.com/company/savagescorp"
            ]
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd({
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Industry 4.0 Solutions",
            "provider": {
              "@type": "Organization",
              "name": "SavagesCorp"
            },
            "description": "Smart manufacturing and digital factory solutions for Industry 4.0 transformation.",
            "areaServed": {
              "@type": "Country",
              "name": "Global"
            }
          })
        }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: serializeJsonLd({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [{
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://savagescorp.ai"
            },{
              "@type": "ListItem",
              "position": 2,
              "name": "Solutions",
              "item": "https://savagescorp.ai/en/solutions"
            },{
              "@type": "ListItem",
              "position": 3,
              "name": "Industry 4.0",
              "item": "https://savagescorp.ai/en/solutions/industry-4-0"
            }]
          })
        }}
      />
    </main>
  );
}
